/**
 * SaveManager — a small localStorage-backed save. For now each chosen
 * character carries its own persistent profile (level, XP, lifetime kills,
 * runs, best depth). This is a temporary local save; it can be swapped for a
 * server/account later without changing callers.
 */
const KEY = "relikara.save.v1";
// Bump SAVE_VERSION whenever a change makes old saves incompatible. On load,
// a stored save with a different version triggers the "update & reset" prompt.
export const SAVE_VERSION = 4;   // v4: bag is a fixed-slot array with null gaps

// The IN-FLIGHT RUN snapshot (data.__run) has its own lightweight version. It is
// NOT part of the SAVE_VERSION reset ladder — a run is disposable, so an
// unreadable/older run is just normalized to safe defaults (Game._normalizeRun)
// rather than wiping the player's meta progress. Bump this when the run shape
// changes so the normalizer can branch on it.
export const RUN_VERSION = 1;    // v1: keys, chests[], openDoors, seed

// Upgrade steps keyed by the version you are LEAVING; each mutates `data` up by
// one version. A missing step (or a downgrade) means the jump isn't recoverable
// and the save is reset. Add an entry whenever SAVE_VERSION is bumped so existing
// progress survives instead of being wiped.
const MIGRATIONS = {
  // v3 → v4: the bag became a fixed-slot array with null gaps. A v3 packed bag is
  // already valid as a v4 bag (items fill from the front, empty slots read as
  // holes), so nothing structural is required.
  3: (data) => data,
};

export class SaveManager {
  constructor() {
    this.data = this._read();
    this.incompatible = false;
    this._dirty = false;
    this._flushTimer = null;
    this.saveFailed = false;     // last write threw (quota / private mode)
    this.onSaveError = null;     // optional hook: notified once when a write fails
    const stored = this.data.__ver;
    if (stored === undefined) {
      // pre-versioning or brand-new save → adopt the current version silently
      this.data.__ver = SAVE_VERSION;
      this.write();
    } else if (stored !== SAVE_VERSION) {
      // try to upgrade in place; only reset if there is no migration path
      if (this._migrate(stored)) { this.data.__ver = SAVE_VERSION; this.write(); }
      else this.incompatible = true;
    }
  }

  /** Run migration steps from `from` up to SAVE_VERSION. Returns true if it reached it. */
  _migrate(from) {
    let v = from;
    while (v < SAVE_VERSION) {
      const step = MIGRATIONS[v];
      if (!step) return false;                 // no path (or a downgrade) → must reset
      try { step(this.data); } catch (_) { return false; }
      v++;
    }
    return v === SAVE_VERSION;
  }

  _read() { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (_) { return {}; } }
  write() {
    this._dirty = false;
    if (this._flushTimer) { clearTimeout(this._flushTimer); this._flushTimer = null; }
    try {
      localStorage.setItem(KEY, JSON.stringify(this.data));
      this.saveFailed = false;
    } catch (_) {                              // private mode / quota
      if (!this.saveFailed) { this.saveFailed = true; if (this.onSaveError) { try { this.onSaveError(); } catch (_) {} } }
    }
  }

  /** Coalesce high-frequency updates (XP / insight / pet XP) into one write per
   *  ~1.2s so combat doesn't stringify the whole save on every kill. Checkpoints
   *  still call write()/flush() for an immediate, guaranteed save. */
  markDirty() {
    this._dirty = true;
    if (this._flushTimer) return;
    this._flushTimer = setTimeout(() => { this._flushTimer = null; if (this._dirty) this.write(); }, 1200);
  }
  /** Force any pending debounced write out now (call before unload / on exit). */
  flush() { if (this._dirty) this.write(); }

  /** Wipe all progress for a breaking update and adopt the new save version. */
  resetForUpdate() {
    try { localStorage.removeItem(KEY); } catch (_) {}
    this.data = { __ver: SAVE_VERSION };
    this.incompatible = false;
    this.write();
  }

  /** Get (creating if needed) a character's profile. */
  profile(id) {
    if (!this.data[id]) this.data[id] = { level: 1, xp: 0, kills: 0, runs: 0, best: 0 };
    const p = this.data[id];
    if (p.bosses == null) p.bosses = 0;            // bosses defeated (achievements)
    if (!p.achievements) p.achievements = {};      // { achId: true }
    if (!p.skin) p.skin = "default";               // chosen recolor skin
    return p;
  }
  /** Mark an achievement earned on a profile; returns true if newly earned. */
  unlockAchievement(id, achId) {
    const p = this.profile(id);
    if (p.achievements[achId]) return false;
    p.achievements[achId] = true; this.write(); return true;
  }

  /**
   * Global Vault meta shared across all heroes:
   *  notoriety — how well the Vault "remembers" the player (rises across runs)
   *  shades    — fallen heroes recorded on death, to return as the Forgotten
   *  codex     — { fragId: true } lore fragments unlocked
   */
  meta() {
    if (!this.data.__meta) this.data.__meta = { notoriety: 0, shades: [], codex: {} };
    const m = this.data.__meta;
    if (m.notoriety == null) m.notoriety = 0;
    if (!m.shades) m.shades = [];
    if (!m.codex) m.codex = {};
    if (m.insight == null) m.insight = 0;    // research currency
    if (!m.research) m.research = {};         // { nodeId: true }
    if (m.deepest == null) m.deepest = 0;     // deepest floor reached (gates research tiers)
    if (!m.stash) m.stash = [];               // persistent Home storage (survives runs/death)
    if (m.bagExpansions == null) m.bagExpansions = 0;       // +1 permanent BAG slot per memory traded, up to +10
    if (m.copper == null) m.copper = 0;                      // copper "fuel" stored at the blacksmith
    if (!m.helix) m.helix = { forged: {}, restored: false };   // Helix: { forged:{codexId:true}, restored }
    if (!m.helix.forged) m.helix.forged = {};
    // m.codex now stores collected MEMORY ids ({ "m12-3": true }); one per helix codex line
    if (!m.pets) m.pets = {};              // { petId: {unlocked,level,xp} } — nothing unlocked at start
    if (m.activePet === undefined) m.activePet = null;
    return m;
  }

  // ---- pets ----
  petState(id) {
    const m = this.meta();
    if (!m.pets[id]) m.pets[id] = { unlocked: false, level: 1, xp: 0 };
    return m.pets[id];
  }
  unlockPet(id) { const p = this.petState(id); if (p.unlocked) return false; p.unlocked = true; this.write(); return true; }
  setActivePet(id) { const p = this.petState(id); if (!p.unlocked) return false; this.meta().activePet = id; this.write(); return true; }
  petXpNeeded(level) { return 40 + level * 30; }
  /** Add XP to a pet; returns the new level if it leveled up, else 0. */
  addPetXp(id, n) {
    const p = this.petState(id); if (!p.unlocked) return 0;
    p.xp += n; let up = 0;
    while (p.level < 20 && p.xp >= this.petXpNeeded(p.level)) { p.xp -= this.petXpNeeded(p.level); p.level++; up = p.level; }
    this.markDirty(); return up;   // hot path (per kill) → coalesced write
  }

  addInsight(n) { const m = this.meta(); m.insight = Math.max(0, (m.insight || 0) + n); this.markDirty(); return m.insight; }
  unlockResearch(id, cost) {
    const m = this.meta();
    if (m.research[id]) return false;
    if ((m.insight || 0) < cost) return false;
    m.insight -= cost; m.research[id] = true; this.write();
    return true;
  }

  /** Add to the Vault's memory of the player. */
  addNotoriety(n) { const m = this.meta(); m.notoriety = Math.max(0, (m.notoriety || 0) + n); this.write(); return m.notoriety; }

  /** Record a fallen hero as a shade the Vault can send back as a Forgotten. */
  recordShade(shade) {
    const m = this.meta();
    m.shades.unshift(shade);
    if (m.shades.length > 12) m.shades.length = 12;   // keep the dozen most recent
    this.write();
  }

  /** Unlock a lore fragment; returns true if it was newly discovered. */
  unlockFragment(id) {
    const m = this.meta();
    if (m.codex[id]) return false;
    m.codex[id] = true; this.write(); return true;
  }

  /** XP required to advance FROM the given level. */
  xpNeeded(level) { return 60 + level * 40; }

  // ---- active run (resume the exact loadout + floor the player left off on) ----
  saveRun(run) { this.data.__run = run; this.write(); }
  getRun() { return this.data.__run || null; }
  hasRun() { return !!this.data.__run; }
  clearRun() { if (this.data.__run) { delete this.data.__run; this.write(); } }

  /** Wipe all saved progress (debug). */
  clear() { try { localStorage.removeItem(KEY); } catch (_) {} this.data = { __ver: SAVE_VERSION }; this.write(); }
}
